import React, { useRef, useState } from 'react';
import {
  AlertCircle,
  Archive,
  CheckCircle,
  FileText,
  FolderUp,
  Image,
  Music,
  Upload,
  Video,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FormModalLayout from '../Common/FormModalLayout';
import { fileService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'mp4',
  'avi',
  'mov',
  'wmv',
  'flv',
  'mp3',
  'wav',
  'flac',
  'aac',
  'pdf',
  'doc',
  'docx',
  'txt',
  'rtf',
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
]);

const FILE_TYPES = {
  image: { icon: Image, color: 'text-green-600 bg-green-100' },
  video: { icon: Video, color: 'text-purple-600 bg-purple-100' },
  audio: { icon: Music, color: 'text-orange-600 bg-orange-100' },
  document: { icon: FileText, color: 'text-blue-600 bg-blue-100' },
  archive: { icon: Archive, color: 'text-gray-600 bg-gray-100' },
  other: { icon: FileText, color: 'text-gray-600 bg-gray-100' },
};

function getFileType(filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'flac', 'aac'].includes(ext)) return 'audio';
  if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(ext)) return 'document';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
  return 'other';
}

function formatFileSize(bytes) {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

export default function FileUploadModal({ isOpen, onClose, onSuccess }) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleFiles = (files) => {
    const rejected = [];
    const accepted = [];
    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_EXTENSIONS.has(ext)) {
        rejected.push(`${file.name} (tipo não permitido)`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        rejected.push(`${file.name} (máx. 20 MB)`);
        continue;
      }
      accepted.push(file);
    }
    if (rejected.length > 0) setError(`Arquivos rejeitados: ${rejected.join(', ')}`);
    if (accepted.length === 0) return;

    setSelectedFiles((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        file,
        id: Math.random().toString(36).slice(2, 11),
        filename: file.name,
        size: file.size,
        type: getFileType(file.name),
        description: '',
        tags: '',
      })),
    ]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e) => {
    if (e.target.files?.[0]) handleFiles(Array.from(e.target.files));
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const updateFileData = (fileId, field, value) => {
    setSelectedFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, [field]: value } : f)));
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setError(null);
    setSuccess(null);
    try {
      await Promise.all(
        selectedFiles.map(async (fileData) => {
          setUploadProgress((prev) => ({ ...prev, [fileData.id]: 0 }));
          try {
            await fileService.upload(fileData.file, {
              description: fileData.description,
              tags: fileData.tags,
            });
            setUploadProgress((prev) => ({ ...prev, [fileData.id]: 100 }));
          } catch (err) {
            setUploadProgress((prev) => ({ ...prev, [fileData.id]: -1 }));
            throw err;
          }
        })
      );
      const successMessage = `${selectedFiles.length} arquivo(s) enviado(s) com sucesso!`;
      setSuccess(successMessage);
      toast.success(successMessage);
      setSelectedFiles([]);
      onSuccess?.();
    } catch (err) {
      const errorMessage = userFacingError(err, 'uploadError');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const resetModal = () => {
    setSelectedFiles([]);
    setUploadProgress({});
    setError(null);
    setSuccess(null);
    setUploading(false);
    setDragActive(false);
  };

  const handleClose = () => {
    if (uploading) return;
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <FormModalLayout
      title="Upload de arquivos"
      subtitle="Envie documentos, imagens e evidências para a central do órgão."
      onClose={uploading ? undefined : handleClose}
      headerIcon={FolderUp}
      size="2xl"
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={uploading} className="btn btn-outline">
            Cancelar
          </button>
          <button
            type="button"
            onClick={uploadFiles}
            disabled={uploading || selectedFiles.length === 0}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            {uploading ? 'Enviando...' : `Enviar ${selectedFiles.length} arquivo(s)`}
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle className="h-5 w-5 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        <div
          className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <p className="mb-2 text-lg font-medium text-gray-900">Arraste arquivos aqui ou clique para selecionar</p>
          <p className="mb-4 text-sm text-gray-500">Imagens, documentos, vídeos e compactados · até 20 MB</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary"
            disabled={uploading}
          >
            Selecionar arquivos
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.pdf,.doc,.docx,.txt,.zip,.rar,.7z"
            onChange={handleFileInput}
            className="hidden"
            disabled={uploading}
          />
        </div>

        {selectedFiles.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900">Arquivos selecionados ({selectedFiles.length})</h3>
            <div className="space-y-3">
              {selectedFiles.map((fileData) => {
                const meta = FILE_TYPES[fileData.type] || FILE_TYPES.other;
                const FileIcon = meta.icon;
                const progress = uploadProgress[fileData.id] || 0;
                const isError = progress === -1;
                const isComplete = progress === 100;
                return (
                  <div key={fileData.id} className="rounded-xl border border-gray-200 p-4">
                    <div className="flex items-start gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${meta.color}`}>
                        <FileIcon className="h-6 w-6" />
                      </div>
                      <div className="min-w-0 flex-1 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h4 className="truncate font-medium text-gray-900">{fileData.filename}</h4>
                            <p className="text-sm text-gray-500">{formatFileSize(fileData.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(fileData.id)}
                            className="rounded-lg p-1 text-gray-400 hover:text-red-600"
                            disabled={uploading}
                            aria-label="Remover arquivo"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                            <input
                              type="text"
                              value={fileData.description}
                              onChange={(e) => updateFileData(fileData.id, 'description', e.target.value)}
                              className="form-input text-sm"
                              placeholder="Descrição do arquivo..."
                              disabled={uploading}
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Tags</label>
                            <input
                              type="text"
                              value={fileData.tags}
                              onChange={(e) => updateFileData(fileData.id, 'tags', e.target.value)}
                              className="form-input text-sm"
                              placeholder="tag1, tag2, tag3..."
                              disabled={uploading}
                            />
                          </div>
                        </div>
                        {uploading && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600">
                                {isError ? 'Erro no upload' : isComplete ? 'Concluído' : 'Enviando...'}
                              </span>
                              <span className="text-gray-500">{isError ? '—' : `${progress}%`}</span>
                            </div>
                            <div className="h-2 w-full rounded-full bg-gray-200">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  isError ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-primary-500'
                                }`}
                                style={{ width: `${Math.abs(progress)}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </FormModalLayout>
  );
}
